/**
 * How a queued write actually reaches the server. Plain `fetch`, so the very
 * same code runs in the page and in the service worker's background sync —
 * the credentials come from IndexedDB, not `localStorage`, for that reason.
 */

import { metaGet, metaSet } from './db';
import type { TransportRequest, TransportResponse } from './outbox';

export const META_API_BASE = 'api-base';
export const META_TOKEN = 'auth-token';
export const META_USER_ID = 'user-id';

export const setApiBase = (base: string): Promise<void> => metaSet(META_API_BASE, base);
export const setAuthToken = (token: string | null): Promise<void> => metaSet(META_TOKEN, token);
export const setUserId = (userId: string | null): Promise<void> => metaSet(META_USER_ID, userId);
export const getUserId = (): Promise<string | undefined | null> => metaGet<string | null>(META_USER_ID);

async function apiBase(): Promise<string> {
  const stored = await metaGet<string>(META_API_BASE);
  if (stored) return stored.replace(/\/$/, '');
  // works in the page and in the worker without naming `self`
  const scope: any = typeof globalThis !== 'undefined' ? globalThis : undefined;
  return scope?.location?.origin ?? '';
}

const queryString = (query?: Record<string, string>): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
};

/**
 * The server's own wording when it has one — never a generic "failed".
 * NestJS validation puts the useful part in `error.details`, so that is what
 * the officer gets to read instead of "Validation failed".
 */
function messageOf(data: any, fallback: string): string {
  const details = data?.error?.details ?? data?.details ?? data?.message;
  if (Array.isArray(details) && details.length) return details.join('; ');
  const message = data?.error?.message ?? data?.message;
  if (Array.isArray(message)) return message.join('; ');
  return typeof message === 'string' && message ? message : fallback;
}

export async function fetchTransport(request: TransportRequest): Promise<TransportResponse> {
  const base = await apiBase();
  const token = await metaGet<string | null>(META_TOKEN);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    // makes a retry safe: the server replays the first outcome instead of
    // creating the record a second time
    'Idempotency-Key': request.idempotencyKey,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (request.file) {
    const form = new FormData();
    form.append('file', request.file.blob, request.file.name);
    Object.entries(request.body ?? {}).forEach(([key, value]) => {
      form.append(key, String(value));
    });
    body = form; // no Content-Type: the browser adds the multipart boundary
  } else if (request.body) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(request.body);
  }

  const response = await fetch(`${base}${request.path}${queryString(request.query)}`, {
    method: request.method,
    headers,
    body,
  });

  let data: any;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }

  return {
    ok: response.ok,
    status: response.status,
    data,
    message: response.ok ? undefined : messageOf(data, `Request failed (${response.status})`),
  };
}
